import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { Clock, KeyRound, ShieldCheck, Sparkles } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { useAuthStore } from '@/lib/auth/store'
import { countdownLabel } from '@/lib/format'
import { Link } from '@/lib/router'
import { useCountdown } from '@/lib/useCountdown'

import { useLogin, useLoginPrecheck } from './api'
import { lastTenantSlug, rememberTenantSlug } from './lastTenant'
import { lockedUntilMsFrom } from './lockout'

export function LoginPage() {
  const navigate = useNavigate()
  const login = useLogin()
  const precheck = useLoginPrecheck()
  const logoutReason = useAuthStore((s) => s.logoutReason)
  const clearLogoutReason = useAuthStore((s) => s.clearLogoutReason)

  const [tenantSlug, setTenantSlug] = useState(lastTenantSlug)
  const [empId, setEmpId] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')

  // Two-step flow: identify (tenant + empId) → precheck decides whether to
  // show the TOTP field → credentials (password [+ totp]). `showTotpField`
  // also flips true reactively if login itself ever comes back
  // totp_code_required despite precheck saying otherwise (race condition,
  // failed precheck call, etc.) — belt-and-braces so the user always has a
  // way to enter a code that's actually needed.
  const [step, setStep] = useState<'identify' | 'credentials'>('identify')
  const [showTotpField, setShowTotpField] = useState(false)

  // Per-account lockout. The API says how long it lasts and we count that
  // down on the local clock, keeping the form shut meanwhile. The server
  // stays authoritative: a submit after local expiry that still comes back
  // account_locked just starts a fresh countdown.
  const [lockedUntil, setLockedUntil] = useState<number | null>(null)
  const lockRemaining = useCountdown(lockedUntil)
  const isLocked = lockedUntil !== null && lockRemaining > 0

  // At zero, drop the alert and re-enable the form.
  const resetLogin = login.reset
  useEffect(() => {
    if (lockedUntil !== null && lockRemaining === 0) {
      setLockedUntil(null)
      resetLogin()
    }
  }, [lockedUntil, lockRemaining, resetLogin])

  function onIdentifySubmit(e: React.FormEvent) {
    e.preventDefault()
    clearLogoutReason()
    precheck.mutate(
      { tenantSlug: tenantSlug.trim(), empId: empId.trim() },
      {
        onSuccess: (data) => {
          setShowTotpField(data.showTotpField)
          setStep('credentials')
        },
        // Precheck failing (network blip, etc.) shouldn't block sign-in —
        // just proceed without the field; the totp_code_required fallback
        // below still catches a genuine need for it.
        onError: () => setStep('credentials'),
      },
    )
  }

  function onCredentialsSubmit(e: React.FormEvent) {
    e.preventDefault()
    // The button is disabled, but Enter in a field still submits the form.
    if (isLocked) return
    login.mutate(
      {
        tenantSlug: tenantSlug.trim(),
        empId: empId.trim(),
        password,
        totpCode: totpCode.trim() || null,
      },
      {
        onSuccess: (data) => {
          // The server accepted this tenant, so it is worth offering back
          // next time. Only on success: a typo must not become the default.
          rememberTenantSlug(tenantSlug.trim())
          // Credentials valid but TOTP isn't enrolled yet — the access
          // token is a short-lived interim one; route into enrol-during-login.
          if (data.totpPending) {
            navigate({ to: '/auth/totp-setup' })
            return
          }
          // Admin-forced reset: the backend gate confines this principal
          // to the change-password surface until they self-change. Route
          // them there instead of the dashboard.
          if (data.passwordResetRequired) {
            navigate({ to: '/auth/change-password' })
            return
          }
          navigate({ to: '/' })
        },
        onError: (err) => {
          const until = lockedUntilMsFrom(err)
          if (until !== null) setLockedUntil(until)
          if (!showTotpField && isAxiosError(err)) {
            const code = (err.response?.data as { code?: string } | undefined)?.code
            if (err.response?.status === 403 && code === 'totp_code_required') {
              setShowTotpField(true)
            }
          }
        },
      },
    )
  }

  // W11.1 ranks 10, 15, 16/21 — the backend now distinguishes:
  //   429 rate_limit_exceeded    → too many attempts; surface Retry-After
  //   403 account_locked         → per-user lockout (5 fails in 15 min)
  //   403 totp_code_required       → TOTP missing on the request
  //   401                          → generic invalid_credentials
  // We read the `code` field from the body where present, then pick the
  // best human message. Fallback chain handles older API versions that
  // don't carry a code.
  const loginErrorMessage = ((): React.ReactNode => {
    if (!login.isError) return null
    if (!isAxiosError(login.error)) return 'Login failed. Please try again.'
    const resp = login.error.response
    if (!resp) return 'Cannot reach the API. Is the backend running on :8080?'

    const body = resp.data as { code?: string; message?: string } | undefined
    const code = body?.code

    // W11.1 rank 10 — 429 + Retry-After. Pull the header (lowercase per
    // axios) and surface a friendly countdown-style message. We don't
    // implement the actual countdown timer here; the message says "try
    // again in {n}s" which is usually all the operator needs.
    if (resp.status === 429) {
      const retryAfter = Number(resp.headers?.['retry-after'] ?? '0')
      return retryAfter > 0
        ? `Too many attempts. Try again in ${retryAfter}s.`
        : 'Too many attempts. Try again in a minute.'
    }

    // W11.1 rank 15 — per-user lockout. Distinct from invalid creds so
    // the user knows retrying won't help until the window passes.
    if (resp.status === 403 && code === 'account_locked') {
      // This API build said how long, so count it down. The Alert is
      // role="alert"; the ticking span opts out of live announcements or a
      // screen reader would read it out every second.
      if (isLocked) {
        return (
          <>
            Too many failed logins. Try again in{' '}
            <span aria-live="off">{countdownLabel(lockRemaining)}</span>, or
            contact your tenant admin.
          </>
        )
      }
      // An older API gives no duration — say what we know.
      return 'Your account is temporarily locked after repeated failed logins. ' +
        'Try again in a few minutes, or contact your tenant admin.'
    }
    if (resp.status === 403 && code === 'totp_code_required') {
      return 'Enter your TOTP code.'
    }
    if (resp.status === 403) {
      return 'Access denied. Check tenant, Emp ID and password.'
    }
    if (resp.status === 401) {
      return 'Invalid credentials.'
    }
    return 'Login failed. Please try again.'
  })()

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Brand panel — only on lg+ */}
      <aside className="relative hidden overflow-hidden bg-navy-900 lg:block">
        <BrandBackdrop />
        <div className="relative z-10 flex h-full flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10 ring-1 ring-inset ring-white/20">
              <span className="text-md font-semibold tracking-tight">G</span>
            </span>
            <div className="leading-tight">
              <p className="text-sm font-semibold tracking-tight">Graviton</p>
              <p className="text-2xs uppercase tracking-wider text-navy-200">Collections platform</p>
            </div>
          </div>

          <div className="max-w-md space-y-6">
            <h1 className="text-3xl font-semibold leading-tight tracking-tight">
              Collections that read the portfolio the way you do.
            </h1>
            <p className="text-base text-navy-100/90">
              Ingest LMS dues, reconcile externally-paid loans, prioritise the
              right cases for the right agents — without harassing customers who
              have already cleared.
            </p>
            <ul className="grid gap-3">
              <Pillar icon={<Sparkles className="h-3.5 w-3.5" />} title="Daily Excel or live LMS sync" body="Same canonical engine, two freshness contracts. Staleness shown honestly." />
              <Pillar icon={<KeyRound className="h-3.5 w-3.5" />} title="Per-tenant isolation" body="Schema + RLS + per-tenant KMS. Audit chain is hash-linked and revoke-only." />
              <Pillar icon={<ShieldCheck className="h-3.5 w-3.5" />} title="RBI conduct guardrails" body="DNC, contact-hour windows and frequency caps fail closed at the API." />
            </ul>
          </div>

          <p className="text-2xs uppercase tracking-wider text-navy-300">
            Multi-tenant collections & recovery
          </p>
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex items-center justify-center bg-navy-50/40 px-4 py-12">
        <div className="w-full max-w-sm space-y-6 animate-fade-in">
          <div className="space-y-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-navy-700 text-white shadow-soft lg:hidden">
              <span className="text-md font-semibold tracking-tight">G</span>
            </span>
            <h2 className="text-2xl font-semibold tracking-tight text-navy-900">Sign in</h2>
            <p className="text-sm text-muted-foreground">
              {step === 'identify'
                ? 'Enter your tenant and Emp ID to continue.'
                : 'Use your password. Managers may also need a TOTP code.'}
            </p>
          </div>

          {/* Session expired notice — only shown when the auth interceptor
              cleared the session in response to a 401, not on a clean
              user-initiated sign-out. Cleared on the next submit so it
              doesn't stack with a fresh login error. */}
          {!loginErrorMessage && logoutReason === 'session_expired' && (
            <Alert variant="default">
              <Clock className="h-4 w-4" />
              <AlertTitle>Your session expired</AlertTitle>
              <AlertDescription>
                Please sign in again to continue. Your work will pick up where
                you left off.
              </AlertDescription>
            </Alert>
          )}

          {/* Idle timeout. Distinct from the 401 notice above: nothing was
              wrong with the token, we closed the session because the handset
              was left unattended — which is the answer the user is actually
              looking for when they find themselves back here. */}
          {!loginErrorMessage && logoutReason === 'inactivity' && (
            <Alert variant="default">
              <Clock className="h-4 w-4" />
              <AlertTitle>Signed out for inactivity</AlertTitle>
              <AlertDescription>
                You were away for a while, so we closed the session to keep
                customer data off an unattended screen. Sign in to continue.
              </AlertDescription>
            </Alert>
          )}

          {step === 'credentials' && loginErrorMessage && (
            <Alert variant="destructive">
              <AlertTitle>{isLocked ? 'Account temporarily locked' : 'Login failed'}</AlertTitle>
              <AlertDescription>{loginErrorMessage}</AlertDescription>
            </Alert>
          )}

          {step === 'identify' && (
            <form onSubmit={onIdentifySubmit} className="space-y-3.5">
              <div className="space-y-1.5">
                <Label htmlFor="tenantSlug">Tenant</Label>
                <Input
                  id="tenantSlug"
                  type="text"
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value)}
                  placeholder="acme-finance"
                  autoComplete="organization"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="empId">Emp ID</Label>
                <Input
                  id="empId"
                  type="text"
                  value={empId}
                  onChange={(e) => setEmpId(e.target.value.toUpperCase())}
                  placeholder="PILOT01"
                  autoComplete="username"
                  required
                  autoFocus
                />
              </div>

              <Button type="submit" className="w-full" disabled={precheck.isPending}>
                {precheck.isPending ? 'Continuing…' : 'Continue'}
              </Button>
            </form>
          )}

          {step === 'credentials' && (
            <form onSubmit={onCredentialsSubmit} className="space-y-3.5">
              <button
                type="button"
                onClick={() => {
                  setStep('identify')
                  login.reset()
                  // A different account on this browser must not inherit the countdown.
                  setLockedUntil(null)
                }}
                className="text-2xs uppercase tracking-wider text-muted-foreground underline"
              >
                {tenantSlug} / {empId} — change
              </button>

              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  autoFocus
                />
              </div>

              {showTotpField && (
                <div className="space-y-1.5">
                  <Label htmlFor="totp">TOTP code</Label>
                  <Input
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    autoComplete="one-time-code"
                    className="font-mono tracking-[0.4em]"
                  />
                </div>
              )}

              <Button type="submit" className="w-full" disabled={login.isPending || isLocked}>
                {login.isPending ? 'Signing in…' : 'Sign in'}
              </Button>
              <Link
                to={`/forgot-password?tenant=${encodeURIComponent(tenantSlug)}&emp=${encodeURIComponent(empId)}`}
                className="block text-center text-xs text-navy-700 underline"
              >
                Forgot password?
              </Link>
            </form>
          )}

          <p className="text-center text-2xs uppercase tracking-wider text-muted-foreground">
            Field agents — use the mobile app to enroll via OTP
          </p>
        </div>
      </main>
    </div>
  )
}

function Pillar({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <li className="flex gap-3">
      <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-white/10 text-white ring-1 ring-inset ring-white/15">
        {icon}
      </span>
      <div>
        <p className="text-sm font-medium text-white">{title}</p>
        <p className="text-xs text-navy-100/80">{body}</p>
      </div>
    </li>
  )
}

function BrandBackdrop() {
  // A faint dot grid + a navy gradient. Operational, not marketing-y.
  return (
    <>
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(at 15% 10%, rgba(74,119,189,0.30), transparent 55%),' +
            'radial-gradient(at 85% 80%, rgba(14,165,183,0.22), transparent 55%),' +
            'linear-gradient(180deg, #0E2138 0%, #16336B 100%)',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.35] mix-blend-overlay"
        style={{
          backgroundImage:
            'radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
      />
    </>
  )
}
